import React, {useCallback, useEffect, useRef, useState} from 'react';
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import {useQuery} from "@tanstack/react-query";
import {Template} from "../../../../../components/template/template.jsx";
import Button from "../../../../../components/button/button.jsx";
import {FaEraser, FaPaperclip, FaPlus, FaSearch} from "react-icons/fa";
import {FaFileExport, FaPrint} from "react-icons/fa6";
import {Card} from "../../../../../components/card/card.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../../utils/aggrid-utils.js";
import solicitationService from "../../../../../services/web/solicitation-service.js";
import projectUtils from "../../../../../utils/project-utils.js";
import {useLoaderData} from "react-router";
import InputDateTime from "../../../../../components/input/input-datetime.jsx";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import natureService from "../../../../../services/web/nature-service.js";
import {useGlobalKey} from "../../../../../hooks/use-global-key.js";
import SentryRow from "../../../../../components/template/sentry-row.jsx";
import ReportUtils from "../../../../../utils/report-utils.js";

const initialFilters = {
    dtStart: "",
    dtEnd: "",
    status: "",
    nature: [],
    description: "",
}

const CustomTooltip = ({ value }) => {
    return (
        <div style={{
            background: "#fff",
            border: "1px solid #dddee0",
            padding: "10px",
            borderRadius: "4px",
            maxWidth: "400px",
            whiteSpace: "normal",
            wordBreak: "break-word",
            boxShadow: "0 2px 8px rgba(0,0,0,.15)",
        }}>
            {value}
        </div>
    );
};

const SolicitationList = () => {
    const loaderData = useLoaderData();
    const navigate = navigateHook();

    const [filter, setFilter] = useState([]);
    const [filterValues, setFilterValues] = useState(initialFilters);
    const [appliedFilters, setAppliedFilters] = useState(initialFilters);

    const {data} = useQuery({
        queryFn: () => solicitationService.list(filter),
        queryKey: ["list", {filter}]
    });

    const {data: reasons} = useQuery({
        queryFn: () => natureService.getAllToSelect(),
        queryKey: ["reasons"]
    });

    const [columnDefs,] = useState([
        { field: "id", headerName: "ID", width: 60},
        { field: "incident_datetime", headerName: "D/H do fato", flex: 1, tooltipValueGetter: ({ value }) => projectUtils.formatDate(value), tooltipComponent: CustomTooltip, valueFormatter: ({value}) => projectUtils.formatDate(value)},
        { field: "applicant", headerName: "Solicitante", flex: 1, tooltipField: "applicant", tooltipComponent: CustomTooltip, comparator: aggridUtils.nullLastComparator},
        { field: "description", headerName: "Descrição da solicitação", flex: 1, tooltipField: "description", tooltipComponent: CustomTooltip, comparator: aggridUtils.nullLastComparator},
        { field: "user_observation", headerName: "Observação do agente", flex: 1, tooltipField: "user_observation", tooltipComponent: CustomTooltip, comparator: aggridUtils.nullLastComparator},
        { field: "nature_name", headerName: "Natureza", flex: 1, tooltipField: "nature_name", tooltipComponent: CustomTooltip, comparator: aggridUtils.nullLastComparator},
        { field: "status", headerName: "Status", flex: 1, tooltipField: "status", tooltipComponent: CustomTooltip, comparator: aggridUtils.nullLastComparator},
        { field: "ecap_id", headerName: loaderData?.ecap_acronym, width: 150, tooltipField: "ecap_id", tooltipComponent: CustomTooltip, comparator: aggridUtils.agGridNumberYearComparator},
        { field: "user_name", headerName: "Cadastrado por", tooltipValueGetter: ({ data: { user_namewar, user_name } }) => user_namewar ? `${user_name} (${user_namewar})` : user_name, flex: 1, tooltipComponent: CustomTooltip, valueFormatter: ({data: {user_namewar, user_name}}) => user_namewar ? `${user_name} (${user_namewar})` : user_name},
        { field: "has_attachments", headerName: "Anexos", width: 85,
            valueGetter: ({ data }) => {
                try {
                    const attaches = data?.attaches?.value;
                    const hasAttachments = JSON.parse(attaches || "[]").length > 0;

                    return hasAttachments ? "Anexos" : "Sem anexos";
                } catch {
                    return "Sem anexos";
                }
            },

            tooltipValueGetter: ({ value }) => value === "Anexos" ? "Contém anexos" : "Não contém anexos",
            comparator: aggridUtils.nullLastComparator,
            tooltipComponent: CustomTooltip,

            cellRenderer: ({ value }) => {
                return value === "Anexos" ? (
                    <div className="h-full w-full flex items-center justify-center"><FaPaperclip size={15}/></div>
                ) : (
                    <div className="h-full w-full cursor-default"/>
                )
            }
        }
    ]);

    const gridRef = useRef(undefined);

    const apply = useCallback(() => {
        setFilter([
            {field: "dateBetween", type: "between", value: {dtIni: filterValues.dtStart, dtEnd: filterValues.dtEnd}},
            {field: "status", type: "like", value: filterValues.status},
            {field: "nature", type: "like", value: filterValues.nature.map(d => d.value).join(",")},
            {field: "description", type: "like", value: filterValues.description},
        ]);

        setAppliedFilters(filterValues);
    }, [filterValues])

    const clear = useCallback(() => {
        setFilterValues(initialFilters);
        setAppliedFilters(initialFilters);
        setFilter([]);
    }, [])

    useGlobalKey("Enter", apply);
    useGlobalKey("Escape", clear);

    const onCellClicked = ({data}) => {
        navigate(`/ecap-solicitation/form/${data.id}`)
    };

    const preparePrint = () => {

        const filters = {
            dtStart: appliedFilters.dtStart || "",
            dtEnd: appliedFilters.dtEnd || "",
            status: appliedFilters.status || "",

            statusLabel: {
                PENDING: "Pendente",
                FINALIZED: "Finalizada com " + loaderData?.ecap_acronym,
                FINALIZED_WITHOUT_ECAP: "Finalizada sem " + loaderData?.ecap_acronym,
            }[appliedFilters.status] ?? "",

            description: appliedFilters.description || "",
            nature: appliedFilters.nature?.map(item => item.value).join(",") ?? ""
        };

        const encoded = btoa(encodeURIComponent(JSON.stringify(filters)));
        navigate(`/reports/solicitation-info?f=${encoded}`, true);
    };

    useEffect(() => {
        const solicitationValues = (data ?? []).map((element) => {
            const hasAttachments = JSON.parse(element?.attaches?.value || "[]").length > 0;

            return [
                element.id,
                projectUtils.formatDate(element.incident_datetime),
                element.applicant ?? "",
                element.description ?? "",
                element.user_observation ?? "",
                element.nature_name ?? "",
                element.status ?? "",
                element.ecap_id ?? "",
                element.user_namewar ? `${element.user_name} (${element.user_namewar})` : element.user_name,
                hasAttachments ? "Sim" : "Não"
            ];
        });

        ReportUtils.createExportableTable(
            ["ID", "D/H do fato", "Solicitante", "Descrição da solicitação", "Observação do agente", "Natureza", "Status", "ECAP", "Cadastrado por", "Anexos"],
            solicitationValues, "Solicitações"
        );
    }, [data]);

    return (
        <Template>
            <Template.Header>
                <Template.Title>Solicitações</Template.Title>

                <div className={"flex flex-row gap-2"}>

                    { data?.length > 0 && (
                        <>
                            <Button target='success' title={'Exportar solicitações'} onClick={() => ReportUtils.export({ exportInSheets: true })}>
                                <FaFileExport/> Exportar
                            </Button>

                            <Button title={'Imprimir solicitações'} onClick={() => { preparePrint() }}>
                                <FaPrint/>
                            </Button>
                        </>
                    )}

                    <Button target={"primary"} onClick={() => navigate("/ecap-solicitation/form")}>
                        <FaPlus/> Adicionar solicitação
                    </Button>

                </div>
            </Template.Header>

            <Template.Body className={"space-y-2"}>
                <Card>
                    <Card.Body>
                        <SentryRow>
                            <div className={"col-span-6 flex items-center"}>
                                <div className="relative flex-1">
                                    <InputDateTime
                                        id={"dtStart"}
                                        label={"Data/Hora do fato entre"}
                                        selected={filterValues.dtStart}
                                        dateFormat="LL"
                                        onChange={(date) => setFilterValues(prev => ({...prev, dtStart: date }))}
                                        customIcon
                                        classNameWrapper={"right-date-input"}
                                    />
                                </div>

                                <div className="border border-[#E5E7EB] px-4 py-2 text-sm leading-none">à</div>

                                <div className="relative flex-1">
                                    <InputDateTime
                                        id={"dtEnd"}
                                        label={""}
                                        selected={filterValues.dtEnd}
                                        dateFormat="LL"
                                        onChange={(date) => setFilterValues(prev => ({...prev, dtEnd: date }))}
                                        classNameWrapper={"left-date-input"}
                                    />
                                </div>
                            </div>

                            <SelectComponent
                                classNameWrapper={"col-span-3"}
                                value={filterValues.status}
                                options={[
                                    {value: "PENDING", label: "Pendente"},
                                    {value: "FINALIZED", label: "Finalizada com " + loaderData?.ecap_acronym},
                                    {value: "FINALIZED_WITHOUT_ECAP", label: "Finalizada sem " + loaderData?.ecap_acronym}
                                ]}
                                label={"Status"}
                                onChange={(value) => setFilterValues(prev => ({...prev, status: value?.value}))}
                            />

                            <SelectComponent
                                classNameWrapper={"col-span-3"}
                                value={filterValues.nature}
                                options={reasons ?? []}
                                label={"Naturezas"}
                                onChange={(value) => setFilterValues(prev => ({...prev, nature: value}))}
                                isMulti
                            />

                            <div className="col-span-12 flex justify-between gap-4">
                                <InputForm
                                    classNameWrapper={"w-full"}
                                    id={"description"}
                                    label={"Descrição"}
                                    value={filterValues.description}
                                    onChange={({target: {value}}) => setFilterValues(prev => ({...prev, description: value}))}
                                />

                                <div className={"flex gap-2"}>
                                    <Button target={"default"} onClick={clear} title={"Limpar"}>
                                        <FaEraser />
                                    </Button>
                                    <Button onClick={apply} title={"Buscar"}>
                                        <FaSearch />
                                    </Button>
                                </div>
                            </div>
                        </SentryRow>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Body>
                        <div className={"aggrid-row-pointer"} style={{height: "calc(100vh - 201px)"}}>
                            <AgGridReact
                                ref={gridRef}
                                rowData={data ?? []}
                                columnDefs={columnDefs}
                                pagination
                                paginationPageSize={100}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus={true}
                                onCellClicked={onCellClicked}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={aggridUtils.generalTheme()}
                                tooltipShowDelay={200}
                            />
                        </div>

                        <div id="chart-tables"></div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default SolicitationList;